import type { DigitizedPrimarySourcePage } from './types';
import type { ViewerState, ViewerAction } from './types';

export function createInitialViewerState({
	pageIndex,
	sources,
}: {
	pageIndex: number;
	sources: DigitizedPrimarySourcePage[];
}): ViewerState {
	return {
		pageIndex,
		isLoading: true,
		hasError: false,
		canZoomIn: false,
		canZoomOut: false,
		isFirstPage: pageIndex === 0,
		isLastPage: pageIndex === sources.length - 1,
		showTranscription: false,
		showTranslation: false,
		showAllSpreads: false,
		showComments: true,
		activeCommentId: null,
		showAboutPanel: false,
		isMobile: false,
		sources,
	};
}

export function viewerReducer(
	state: ViewerState,
	action: ViewerAction,
): ViewerState {
	switch (action.type) {
		case 'set_has_error':
			return { ...state, hasError: true };
		case 'clear_error':
			return { ...state, hasError: false };
		case 'start_loading':
			return { ...state, isLoading: true };
		case 'stop_loading':
			return { ...state, isLoading: false };
		case 'set_can_zoom_in':
			return { ...state, canZoomIn: action.canZoomIn };
		case 'set_can_zoom_out':
			return { ...state, canZoomOut: action.canZoomOut };
		case 'show_transcription':
			return {
				...state,
				showTranscription: true,
				showAboutPanel: false,
				activeCommentId: null,
				...(state.isMobile ? { showTranslation: false } : null),
			};
		case 'hide_transcription':
			return { ...state, showTranscription: false };
		case 'show_translation':
			return {
				...state,
				showTranslation: true,
				showAboutPanel: false,
				activeCommentId: null,
				...(state.isMobile ? { showTranscription: false } : null),
			};
		case 'hide_translation':
			return { ...state, showTranslation: false };
		case 'hide_reading_tools':
			return { ...state, showTranscription: false, showTranslation: false };
		case 'toggle_all_spreads':
			return {
				...state,
				showAllSpreads: !state.showAllSpreads,
			};
		case 'go_to_page':
			return {
				...state,
				hasError: false,
				showAboutPanel: false,
				activeCommentId: null,
				...generatePageState(action.index, state),
			};
		case 'go_to_next_page':
			return {
				...state,
				hasError: false,
				showAboutPanel: false,
				activeCommentId: null,
				...generatePageState(state.pageIndex + 1, state),
			};
		case 'go_to_previous_page':
			return {
				...state,
				hasError: false,
				showAboutPanel: false,
				activeCommentId: null,
				...generatePageState(state.pageIndex - 1, state),
			};
		case 'show_comments':
			return { ...state, showComments: true };
		case 'hide_comments':
			return { ...state, showComments: false };
		case 'toggle_comments':
			return { ...state, showComments: !state.showComments };
		case 'show_comment':
			return {
				...state,
				activeCommentId: action.commentId,
				showAboutPanel: false,
				...(state.isMobile
					? {
							showTranscription: false,
							showTranslation: false,
						}
					: null),
			};
		case 'hide_shown_comment':
			return { ...state, activeCommentId: null };
		case 'show_about_panel':
			return {
				...state,
				showAboutPanel: true,
				activeCommentId: null,
				...(state.isMobile
					? { showTranscription: false, showTranslation: false }
					: null),
			};
		case 'hide_about_panel':
			return { ...state, showAboutPanel: false };
		case 'toggle_about_panel': {
			const newShowAboutPanel = !state.showAboutPanel;
			const newState = { ...state, showAboutPanel: newShowAboutPanel };

			if (newShowAboutPanel) {
				newState.activeCommentId = null;
				if (state.isMobile) {
					newState.showTranscription = false;
					newState.showTranslation = false;
				}
			}

			return newState;
		}
		case 'set_is_mobile':
			return { ...state, isMobile: action.isMobile };
		default:
			return state;
	}
}

function generatePageState(index: number, state: ViewerState) {
	let pageIndex = index;

	if (pageIndex < 0) {
		pageIndex = 0;
	}

	if (pageIndex >= state.sources.length) {
		pageIndex = state.sources.length - 1;
	}

	return {
		pageIndex,
		isFirstPage: pageIndex === 0,
		isLastPage: pageIndex === state.sources.length - 1,
	};
}
